// The page's state: one store every component reads and subscribes to, plus the URL.
//
// Search params are read once at load (`?run=`, `?trace=` and friends decide what to
// open); the
// hash carries the view (window, expanded rows, selection, UI) so a link reproduces
// what someone was looking at. The hash is written with replaceState, debounced, so
// panning does not flood the history.

import { debounce } from "./dom.js";

export function createStore(init) {
  const state = { ...init };
  const subs = new Map();
  const all = new Set();
  function emit(keys) {
    for (const k of keys) for (const fn of subs.get(k) || []) fn(state, k);
    for (const fn of all) fn(state, keys);
  }
  return {
    get: () => state,
    set(patch) {
      const changed = [];
      for (const k in patch) {
        if (state[k] !== patch[k]) {
          state[k] = patch[k];
          changed.push(k);
        }
      }
      if (changed.length) emit(changed);
      return changed.length > 0;
    },
    on(key, fn) {
      if (key === "*") { all.add(fn); return () => all.delete(fn); }
      if (!subs.has(key)) subs.set(key, new Set());
      subs.get(key).add(fn);
      return () => subs.get(key).delete(fn);
    },
  };
}

export function readUrl() {
  const q = new URLSearchParams(location.search);
  const shape = q.get("shape");
  // A run by its key (24 hex): where it is, js/load.js decides. `?trace=` is the
  // same parameter under its old name. Anything else is not a key and is ignored,
  // which leaves the welcome page: the page never fetches a URL a visitor chose.
  const run = q.get("run") || q.get("trace");
  return {
    run: /^[0-9a-f]{24}$/.test(run || "") ? run : null,
    gen: q.get("gen"),
    shape: shape ? shape.split(",").map(Number) : null,
    ui: q.get("ui"),
  };
}

export function readHash() {
  const out = {};
  const h = location.hash.replace(/^#/, "");
  if (!h) return out;
  for (const part of h.split("&")) {
    const i = part.indexOf("=");
    if (i < 0) continue;
    out[decodeURIComponent(part.slice(0, i))] = decodeURIComponent(part.slice(i + 1));
  }
  return out;
}

export const writeHash = debounce((obj) => {
  const parts = [];
  for (const k in obj) {
    const v = obj[k];
    if (v == null || v === "" || v === false) continue;
    parts.push(encodeURIComponent(k) + "=" + encodeURIComponent(v));
  }
  const h = parts.length ? "#" + parts.join("&") : "";
  if (h !== location.hash) history.replaceState(null, "", location.pathname + location.search + h);
}, 200);
